var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/Collapsible/provider.tsx
import React, { useState } from "react";

// src/contextEx/create.ts
import { createContext } from "react";
var createContextEx = /* @__PURE__ */ __name(() => createContext({
  provided: false
}), "createContextEx");

// src/contextEx/use.ts
import { use } from "react";
var useContextEx = /* @__PURE__ */ __name((context, contextName, required = true) => {
  const { provided, ...props } = use(context);
  if (!provided && required) {
    throw new Error(`use${contextName} can not be used outside of a ${contextName}Context when required=true`);
  }
  return props;
}, "useContextEx");
var useProvided = /* @__PURE__ */ __name((context) => {
  const { provided } = use(context);
  return provided;
}, "useProvided");

// src/Collapsible/context.ts
var CollapsibleContext = createContextEx();

// src/Collapsible/provider.tsx
var CollapsibleProvider = /* @__PURE__ */ __name(({ defaultCollapse = false, defaultCollapseEnd = false, children }) => {
  const [collapse, setCollapse] = useState(() => defaultCollapse);
  const [collapseEnd, setCollapseEnd] = useState(() => defaultCollapseEnd);
  return (
    // eslint-disable-next-line @eslint-react/no-unstable-context-value
    /* @__PURE__ */ React.createElement(CollapsibleContext, {
      value: {
        collapse,
        collapseEnd,
        provided: true,
        setCollapse,
        setCollapseEnd
      }
    }, children)
  );
}, "CollapsibleProvider");

// src/Collapsible/use.ts
var useCollapsible = /* @__PURE__ */ __name(() => useContextEx(CollapsibleContext, "Collapsible", false), "useCollapsible");

// src/components/BusyCircularProgress.tsx
import { alpha, Box, CircularProgress, useTheme } from "@mui/material";
import React2 from "react";
var BusyCircularProgress = /* @__PURE__ */ __name(({ bgcolor, style, rounded, size, opacity = 0.85, ...props }) => {
  const theme = useTheme();
  return /* @__PURE__ */ React2.createElement(Box, {
    display: "flex",
    bgcolor: alpha(bgcolor ?? theme.palette.background.default, Number.parseFloat(`${opacity}`)),
    flexGrow: 1,
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    style: {
      borderRadius: rounded ? theme.shape.borderRadius : 0,
      ...style
    }
  }, /* @__PURE__ */ React2.createElement(CircularProgress, {
    size,
    ...props
  }));
}, "BusyCircularProgress");

// src/components/BusyLinearProgress.tsx
import { alpha as alpha2, Box as Box2, LinearProgress, useTheme as useTheme2 } from "@mui/material";
import React3 from "react";
var BusyLinearProgress = /* @__PURE__ */ __name(({ bgcolor, style, rounded, opacity = 0.85, ...props }) => {
  const theme = useTheme2();
  return /* @__PURE__ */ React3.createElement(Box2, {
    display: "flex",
    flexDirection: "column",
    bgcolor: alpha2(bgcolor ?? theme.palette.background.default, Number.parseFloat(`${opacity}`)),
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: "flex-start",
    alignItems: "stretch",
    overflow: "hidden",
    style: {
      borderRadius: rounded ? theme.shape.borderRadius : 0,
      ...style
    }
  }, /* @__PURE__ */ React3.createElement(LinearProgress, props));
}, "BusyLinearProgress");

// src/components/NotFound.tsx
import { Box as Box3, Typography } from "@mui/material";
import React4 from "react";
var NotFound = /* @__PURE__ */ __name(({ sx, ...props }) => {
  return /* @__PURE__ */ React4.createElement(Box3, {
    sx: {
      display: "flex",
      flexDirection: "column",
      ...sx
    },
    ...props
  }, /* @__PURE__ */ React4.createElement(Typography, {
    variant: "h2"
  }, "Sorry!"), /* @__PURE__ */ React4.createElement(Typography, {
    marginY: 3,
    variant: "body2"
  }, "Can't find anything here"));
}, "NotFound");

// src/hooks/useBreakpoint.ts
import { useMediaQuery, useTheme as useTheme3 } from "@mui/material";
var useBreakpoint = /* @__PURE__ */ __name(() => {
  const theme = useTheme3();
  const xs = useMediaQuery(theme.breakpoints.only("xs"));
  const sm = useMediaQuery(theme.breakpoints.only("sm"));
  const md = useMediaQuery(theme.breakpoints.only("md"));
  const lg = useMediaQuery(theme.breakpoints.only("lg"));
  const xl = useMediaQuery(theme.breakpoints.only("xl"));
  if (xs) {
    return "xs";
  } else if (sm) {
    return "sm";
  } else if (md) {
    return "md";
  } else if (lg) {
    return "lg";
  } else if (xl) {
    return "xl";
  }
}, "useBreakpoint");

// src/hooks/useInterval.ts
import { useEffect, useRef } from "react";
var useInterval = /* @__PURE__ */ __name((callback, delay) => {
  const intervalRef = useRef(-1);
  const savedCallback = useRef(callback);
  useEffect(() => {
    savedCallback.current = callback;
  }, [
    callback
  ]);
  useEffect(() => {
    const tick = /* @__PURE__ */ __name(() => savedCallback.current(), "tick");
    intervalRef.current = globalThis.setInterval(tick, delay);
    return () => globalThis.clearInterval(intervalRef.current);
  }, [
    delay
  ]);
  return intervalRef;
}, "useInterval");

// src/hooks/useLocalStorage.ts
import { useCallback, useState as useState2 } from "react";

// src/lib/getApiStage.ts
import { getApiStage as baseGetApiStage } from "@xylabs/api";
var getApiStage = /* @__PURE__ */ __name(() => {
  return baseGetApiStage(document.location.hostname);
}, "getApiStage");

// src/lib/isLocalHost.ts
var isLocalhost = /* @__PURE__ */ __name(() => !!(globalThis.location.hostname === "localhost" || globalThis.location.hostname === "[::1]" || /^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d{1,2})){3}$/.test(globalThis.location.hostname)), "isLocalhost");

// src/lib/profile.ts
var logProfiling = false;
var enableProfileLogging = /* @__PURE__ */ __name((enabled) => {
  logProfiling = enabled;
}, "enableProfileLogging");
var profileResults = [];
var profileBlock = /* @__PURE__ */ __name(async (name, closure) => {
  const startTime = Date.now();
  await closure();
  const endTime = Date.now();
  profileResults.push({
    endTime,
    name,
    startTime
  });
  if (logProfiling) {
    console.log(`Timed ${name} [${endTime - startTime}ms]`);
  }
}, "profileBlock");

// src/lib/safeLocalStorage.ts
var getLocalStorageObject = /* @__PURE__ */ __name((key, log) => {
  let result = {};
  try {
    result = JSON.parse(localStorage.getItem(key) ?? "{}");
  } catch (e) {
    log?.error("getLocalStorageObject", e);
  }
  return result;
}, "getLocalStorageObject");
var setLocalStorageObject = /* @__PURE__ */ __name((key, value, log) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    log?.error("setLocalStorageObject", e);
  }
}, "setLocalStorageObject");

// src/lib/safeSessionStorage.ts
var getSessionStorageObject = /* @__PURE__ */ __name((key, log) => {
  let result = {};
  try {
    result = JSON.parse(sessionStorage.getItem(key) ?? "{}");
  } catch (e) {
    log?.error("getSessionStorageObject", e);
  }
  return result;
}, "getSessionStorageObject");
var setSessionStorageObject = /* @__PURE__ */ __name((key, value, log) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    log?.error("setSessionStorageObject", e);
  }
}, "setSessionStorageObject");

// src/hooks/useLocalStorage.ts
var useLocalStorage = /* @__PURE__ */ __name((key, defaultValue) => {
  const [storedValue, setStoredValue] = useState2(() => {
    try {
      const item = getLocalStorageObject(key);
      if (typeof item === "boolean" || typeof item === "string" || typeof item === "number") {
        return item;
      } else if (Array.isArray(item)) {
        return item;
      } else if (Object.keys(item).length > 0) {
        return item;
      } else {
        setLocalStorageObject(key, defaultValue);
        return defaultValue;
      }
    } catch {
      return defaultValue;
    }
  });
  const setValue = useCallback((value) => {
    setStoredValue(value);
    setLocalStorageObject(key, value);
  }, [
    setStoredValue,
    key
  ]);
  return [
    storedValue,
    setValue
  ];
}, "useLocalStorage");

// src/hooks/useMediaQuery.ts
import { useMediaQuery as useMediaQuery2 } from "@mui/material";

// src/hooks/useMounted.tsx
import { useEffect as useEffect2, useState as useState3 } from "react";
var useMounted = /* @__PURE__ */ __name(() => {
  const [mounted, setMounted] = useState3(true);
  useEffect2(() => {
    setMounted(true);
    return () => {
      setMounted(false);
    };
  }, []);
  return () => {
    return mounted;
  };
}, "useMounted");

// src/hooks/useSpacing.tsx
import { useTheme as useTheme4 } from "@mui/material";
var useSpacing = /* @__PURE__ */ __name((value) => {
  const theme = useTheme4();
  return theme.spacing(typeof value === "string" ? Number.parseInt(value) : value);
}, "useSpacing");

// src/hooks/useTimeout.ts
import { useEffect as useEffect3, useRef as useRef2 } from "react";
var useTimeout = /* @__PURE__ */ __name((callback, delay) => {
  const timeoutRef = useRef2(-1);
  const savedCallback = useRef2(callback);
  useEffect3(() => {
    savedCallback.current = callback;
  }, [
    callback
  ]);
  useEffect3(() => {
    const tick = /* @__PURE__ */ __name(() => savedCallback.current(), "tick");
    timeoutRef.current = globalThis.setTimeout(tick, delay);
    return () => globalThis.clearTimeout(timeoutRef.current);
  }, [
    delay
  ]);
  return timeoutRef;
}, "useTimeout");

// src/hooks/useWebP.tsx
import { useAsyncEffect } from "@xylabs/react-async-effect";
import { useState as useState4 } from "react";
var supportsWebP;
var useWebP = /* @__PURE__ */ __name((webp, alt) => {
  const [img, setImg] = useState4();
  useAsyncEffect(async (mounted) => {
    if (supportsWebP === void 0) {
      supportsWebP = await getSupportsWebP();
    }
    if (mounted()) {
      setImg(supportsWebP ? webp : alt);
    }
  }, [
    alt,
    webp
  ]);
  return img;
}, "useWebP");
var getSupportsWebP = /* @__PURE__ */ __name(async () => {
  return await new Promise((resolve) => {
    const image = new Image();
    const onError = /* @__PURE__ */ __name(() => resolve(false), "onError");
    image.addEventListener("error", onError);
    const onLoad = /* @__PURE__ */ __name(() => resolve(image.width === 1), "onLoad");
    image.addEventListener("load", onLoad);
    image.src = "data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=";
  }).catch(() => false);
}, "getSupportsWebP");

// src/hooks/useWindowSize.ts
import { useCallback as useCallback2, useEffect as useEffect4, useState as useState5 } from "react";
function useWindowSize() {
  const [windowSize, setWindowSize] = useState5({
    height: window.innerHeight,
    width: window.innerWidth
  });
  const handleResize = useCallback2(() => {
    setWindowSize({
      height: window.innerHeight,
      width: window.innerWidth
    });
  }, []);
  useEffect4(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [
    handleResize
  ]);
  return windowSize;
}
__name(useWindowSize, "useWindowSize");

// src/calcSpacing.ts
var calcSpacing = /* @__PURE__ */ __name((theme, values) => {
  for (const value of values) {
    if (value !== void 0) {
      if (typeof value === "string") {
        return value;
      } else if (typeof value === "number") {
        return theme.spacing(value);
      }
    }
  }
}, "calcSpacing");

// src/mergeBoxlikeStyles.ts
var mergeBoxlikeStyles = /* @__PURE__ */ __name((theme, props, defaultProps) => {
  const { alignContent, alignItems, alignSelf, bgcolor, border, borderColor, display, flexGrow, flexShrink, flexDirection, flexWrap, justifyContent, justifyItems, justifySelf, height, minHeight, minWidth, margin, marginX, marginY, marginTop, marginLeft, marginRight, marginBottom, padding, paddingX, paddingY, paddingTop, paddingLeft, paddingRight, paddingBottom, position, style, width, ...rootProps } = {
    ...defaultProps,
    ...props
  };
  const mergedStyle = {
    alignContent,
    alignItems,
    alignSelf,
    backgroundColor: bgcolor,
    border,
    borderColor,
    display,
    flexDirection,
    flexGrow,
    flexShrink,
    flexWrap,
    height,
    justifyContent,
    justifyItems,
    justifySelf,
    marginBottom: calcSpacing(theme, [
      marginBottom,
      marginY,
      margin
    ]),
    marginLeft: calcSpacing(theme, [
      marginLeft,
      marginX,
      margin
    ]),
    marginRight: calcSpacing(theme, [
      marginRight,
      marginX,
      margin
    ]),
    marginTop: calcSpacing(theme, [
      marginTop,
      marginY,
      margin
    ]),
    minHeight,
    minWidth,
    paddingBottom: calcSpacing(theme, [
      paddingBottom,
      paddingY,
      padding
    ]),
    paddingLeft: calcSpacing(theme, [
      paddingLeft,
      paddingX,
      padding
    ]),
    paddingRight: calcSpacing(theme, [
      paddingRight,
      paddingX,
      padding
    ]),
    paddingTop: calcSpacing(theme, [
      paddingTop,
      paddingY,
      padding
    ]),
    position,
    width,
    ...style
  };
  return {
    ...rootProps,
    style: mergedStyle
  };
}, "mergeBoxlikeStyles");
export {
  BusyCircularProgress,
  BusyLinearProgress,
  CollapsibleProvider,
  NotFound,
  createContextEx,
  enableProfileLogging,
  getApiStage,
  getLocalStorageObject,
  getSessionStorageObject,
  isLocalhost,
  mergeBoxlikeStyles,
  profileBlock,
  profileResults,
  setLocalStorageObject,
  setSessionStorageObject,
  useBreakpoint,
  useCollapsible,
  useContextEx,
  useInterval,
  useLocalStorage,
  useMediaQuery2 as useMediaQuery,
  useMounted,
  useProvided,
  useSpacing,
  useTimeout,
  useWebP,
  useWindowSize
};
//# sourceMappingURL=index.mjs.map